// Response-deadline warning levels for negative customer experience feedback.
//
// The clock starts when the customer submitted their complaint
// (`messageSubmittedAt`). The normal target for a first response is 1 hour;
// after that the entry escalates through orange and red until, past 24 hours,
// it is considered overdue and drives the hourly reminder pop-ups.
//
//   green    ≤ 1h   — within the normal response window
//   orange   ≤ 3h   — getting late
//   red      ≤ 24h  — must be answered soon
//   overdue  > 24h  — deadline blown, hourly reminders fire
//
// Only unresolved feedback (status new/inProgress) with a submitted message has
// a live deadline; everything else returns level `none`.

export const FEEDBACK_DEADLINE_MS = {
  green: 1 * 60 * 60 * 1000, // 1h
  orange: 3 * 60 * 60 * 1000, // 3h
  red: 24 * 60 * 60 * 1000, // 24h
};

// Map each level to a MUI palette color + a short German label used on chips.
export const FEEDBACK_DEADLINE_LEVELS = {
  green: { color: 'success', label: 'Frist offen' },
  orange: { color: 'warning', label: 'Wird knapp' },
  red: { color: 'error', label: 'Dringend' },
  overdue: { color: 'error', label: 'Überfällig' },
  none: { color: 'default', label: '' },
};

/**
 * Returns the warning level for a feedback entry.
 * @param {object} feedback - feedback item (needs status + messageSubmittedAt)
 * @param {number} [nowMs] - reference timestamp (defaults to Date.now())
 * @returns {{ level: string, color: string, label: string, ageMs: number|null }}
 */
export function getFeedbackDeadline(feedback, nowMs = Date.now()) {
  const none = { level: 'none', ...FEEDBACK_DEADLINE_LEVELS.none, ageMs: null };
  if (!feedback) return none;

  // Resolved (or otherwise not open) entries no longer have a running deadline.
  if (feedback.status === 'resolved') return none;

  const submittedAt = feedback.messageSubmittedAt;
  if (!submittedAt) return none;

  const submittedMs = new Date(submittedAt).getTime();
  if (!submittedMs || Number.isNaN(submittedMs)) return none;

  const ageMs = Math.max(0, nowMs - submittedMs);

  let level;
  if (ageMs <= FEEDBACK_DEADLINE_MS.green) level = 'green';
  else if (ageMs <= FEEDBACK_DEADLINE_MS.orange) level = 'orange';
  else if (ageMs <= FEEDBACK_DEADLINE_MS.red) level = 'red';
  else level = 'overdue';

  return { level, ...FEEDBACK_DEADLINE_LEVELS[level], ageMs };
}

// Escalation stages. A complaint moves KI → Callcenter → Standort purely by age,
// with the same 1h / 3h thresholds as the deadline bands. Staff only ever see
// feedback that is currently at a stage they belong to (enforced by the backend
// via the customerExperienceFeedback:escalation:* rights); this mapping drives
// the stage indicator chip.
export const FEEDBACK_ESCALATION_STAGES = {
  ai: { label: 'KI', color: 'info' },
  callcenter: { label: 'Callcenter', color: 'warning' },
  location: { label: 'Standort', color: 'error' },
  none: { label: '', color: 'default' },
};

/**
 * Returns the current escalation stage for a feedback entry, derived from the
 * same age bands as getFeedbackDeadline.
 * @returns {{ stage: string, label: string, color: string }}
 */
export function getEscalationStage(feedback, nowMs = Date.now()) {
  const { level } = getFeedbackDeadline(feedback, nowMs);
  let stage;
  if (level === 'green') stage = 'ai';
  else if (level === 'orange') stage = 'callcenter';
  else if (level === 'red' || level === 'overdue') stage = 'location';
  else stage = 'none';
  return { stage, ...FEEDBACK_ESCALATION_STAGES[stage] };
}

/**
 * Human-readable elapsed time since the complaint arrived, e.g. "vor 2 Std."
 * or "vor 35 Min.". Returns '' when no age is available.
 */
export function formatFeedbackAge(ageMs) {
  if (ageMs == null) return '';
  const minutes = Math.floor(ageMs / 60000);
  if (minutes < 60) return `vor ${Math.max(1, minutes)} Min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  return `vor ${days} Tg.`;
}
